import { useEffect, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { NavRail } from "./NavRail";
import { BottomNav } from "./BottomNav";
import { TopBar } from "./TopBar";
import { StatusBar } from "./StatusBar";
import { WelcomeTour } from "./WelcomeTour";
import { useBackendOnline } from "./useClock";

const COLLAPSE_KEY = "vk.nav.collapsed";

function readCollapsed(): boolean {
	try {
		return localStorage.getItem(COLLAPSE_KEY) === "1";
	} catch {
		return false;
	}
}

function useMedia(query: string): boolean {
	const [match, setMatch] = useState(() => window.matchMedia(query).matches);
	useEffect(() => {
		const mq = window.matchMedia(query);
		const onChange = () => setMatch(mq.matches);
		onChange();
		mq.addEventListener("change", onChange);
		return () => mq.removeEventListener("change", onChange);
	}, [query]);
	return match;
}

/**
 * The page frame, by screen size:
 * - phone (< 768px): top bar, content, and a tab bar at the bottom — no rail,
 *   so pages get the full width; the Pro switch and the connection dot move
 *   into the top bar;
 * - tablet (768–1023px): the rail as icons only;
 * - desktop: the rail with labels (collapsible), and the status strip.
 */
export function AppShell({ children }: { children: ReactNode }) {
	const [preferCollapsed, setCollapsed] = useState(readCollapsed);
	const phone = useMedia("(max-width: 767px)");
	const compact = useMedia("(max-width: 1023px)");
	const collapsed = compact || preferCollapsed;
	const online = useBackendOnline();

	useEffect(() => {
		try {
			localStorage.setItem(COLLAPSE_KEY, preferCollapsed ? "1" : "0");
		} catch {
			/* storage unavailable — preference just isn't remembered */
		}
	}, [preferCollapsed]);

	return (
		<div className="min-h-screen bg-background">
			{!phone && <NavRail collapsed={collapsed} canToggle={!compact} onToggle={() => setCollapsed((c) => !c)} />}
			<div className={cn("flex min-h-screen min-w-0 flex-col transition-[padding] duration-200", phone ? "" : collapsed ? "pl-16" : "pl-56")}>
				<TopBar phone={phone} online={online} />
				<main
					className={cn(
						"min-w-0 flex-1 px-4 py-4 sm:px-5 sm:py-5",
						// Clear the tab bar (and the home indicator on notched phones).
						phone && "pb-[calc(4.5rem+env(safe-area-inset-bottom))]",
					)}
				>
					{children}
				</main>
				{!phone && <StatusBar online={online} />}
			</div>
			{phone && <BottomNav />}
			<WelcomeTour />
		</div>
	);
}
